import React, { useMemo } from 'react';
import {
  BsDownload,
  BsArrowRight,
  BsBarChartLine,
  BsCamera,
  BsGraphUpArrow,
  BsBoxArrowInDown,
  BsPersonCheck,
} from 'react-icons/bs';
import { FaWindows, FaApple, FaLinux } from 'react-icons/fa';
import classNames from 'classnames';
import { desktopAppDownloadUrls } from '@views/Onboarding/Onboarding.constants';
import styles from './DownloadDesktopAppPage.module.scss';

const detectOS = () => {
  const ua = navigator.userAgent;
  if (/Windows/i.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'macos';
  if (/Linux/i.test(ua)) return 'linux';
  return 'windows';
};

const PLATFORMS = [
  {
    key: 'windows',
    icon: <FaWindows size={28} color="#0078d4" />,
    iconBg: '#e8f4ff',
    name: 'Windows',
    desc: 'For Windows 10 and above',
    downloadUrl: desktopAppDownloadUrls.windows,
    btnLabel: 'Download for Windows',
  },
  {
    key: 'macos',
    icon: <FaApple size={28} color="#1a1a1a" />,
    iconBg: '#f2f4f7',
    name: 'macOS',
    desc: 'For macOS 11+ (Big Sur and above)',
    downloadUrl: desktopAppDownloadUrls.macos,
    btnLabel: 'Download for macOS',
  },
  {
    key: 'linux',
    icon: <FaLinux size={28} color="#1a1a1a" />,
    iconBg: '#f2f4f7',
    name: 'Linux',
    desc: 'For Ubuntu, Debian and other distros',
    downloadUrl: desktopAppDownloadUrls.linux,
    btnLabel: 'Download for Linux',
  },
];

const FEATURES = [
  {
    icon: <BsBarChartLine size={16} color="#3363F5" />,
    title: 'Real-time activity tracking',
    desc: 'Monitor apps, websites and productivity',
  },
  {
    icon: <BsCamera size={16} color="#039855" />,
    title: 'Automatic screenshots',
    desc: 'Secure and encrypted data capture',
  },
  {
    icon: <BsGraphUpArrow size={16} color="#6938EF" />,
    title: 'Workforce insights',
    desc: 'Get actionable reports and analytics',
  },
];

const INSTALL_STEPS = [
  {
    icon: <BsDownload size={20} color="#3363F5" />,
    title: 'Download App',
    desc: 'Get the desktop application',
  },
  {
    icon: <BsBoxArrowInDown size={20} color="#039855" />,
    title: 'Install',
    desc: 'Run the installer and follow steps',
  },
  {
    icon: <BsPersonCheck size={20} color="#6938EF" />,
    title: 'Sign In',
    desc: 'Use your Prodaff account',
  },
  {
    icon: <BsGraphUpArrow size={20} color="#F79009" />,
    title: 'Start Tracking',
    desc: 'Activity tracking begins automatically',
  },
];

export const DownloadDesktopAppPageComponent = () => {
  const detectedOS = useMemo(detectOS, []);

  return (
    <div className={styles.page}>
      <div className={styles.panel}>
        {/* Platform cards */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Download for your platform</h3>
          <div className={styles.platformGrid} data-tour="download-buttons">
            {PLATFORMS.map((p) => (
              <div
                key={p.key}
                className={classNames(styles.platformCard, {
                  [styles.platformCardDetected]: p.key === detectedOS,
                })}
              >
                <div className={styles.platformCardTop}>
                  <div
                    className={styles.platformIconWrap}
                    style={{ backgroundColor: p.iconBg }}
                  >
                    {p.icon}
                  </div>
                  <div className={styles.platformInfo}>
                    <div className={styles.platformTitleRow}>
                      <span className={styles.platformName}>{p.name}</span>
                      {p.key === detectedOS && (
                        <span className={styles.detectedBadge}>
                          Recommended
                        </span>
                      )}
                    </div>
                    <p className={styles.platformDesc}>{p.desc}</p>
                  </div>
                </div>
                {p.downloadUrl ? (
                  <a
                    href={p.downloadUrl}
                    className={classNames(styles.downloadBtn, {
                      [styles.downloadBtnPrimary]: p.key === detectedOS,
                      [styles.downloadBtnOutline]: p.key !== detectedOS,
                    })}
                    rel="noopener noreferrer"
                  >
                    <BsDownload size={13} />
                    {p.btnLabel}
                  </a>
                ) : (
                  <button
                    type="button"
                    className={styles.downloadBtnDisabled}
                    disabled
                  >
                    <BsDownload size={13} />
                    Coming soon
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>What you get</h3>
          <div className={styles.featuresRow}>
            {FEATURES.map((feat) => (
              <div key={feat.title} className={styles.featureItem}>
                <div className={styles.featureIconWrap}>{feat.icon}</div>
                <div className={styles.featureText}>
                  <span className={styles.featureTitle}>{feat.title}</span>
                  <span className={styles.featureDesc}>{feat.desc}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Installation steps */}
        <div className={styles.section}>
          <h3 className={styles.sectionTitle}>Installation steps</h3>
          <div className={styles.stepsRow}>
            {INSTALL_STEPS.map((step, i) => (
              <React.Fragment key={step.title}>
                <div className={styles.step}>
                  <div className={styles.stepIconWrap}>{step.icon}</div>
                  <span className={styles.stepTitle}>{step.title}</span>
                  <p className={styles.stepDesc}>{step.desc}</p>
                </div>
                {i < INSTALL_STEPS.length - 1 && (
                  <BsArrowRight size={16} className={styles.stepArrow} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
